import { useState } from 'react';
import { Product } from '../../data/products';
import { 
  PrintTemplate, 
  PRINT_TEMPLATES, 
  generatePrintTemplate,
  downloadPrintTemplate 
} from '../utils/generatePrintTemplate';
import { 
  Download, 
  Printer, 
  Tag, 
  Package, 
  DollarSign, 
  ClipboardList, 
  LayoutGrid,
  X,
  Check,
  Loader2,
} from 'lucide-react';

interface BarcodePrintTemplatesProps {
  product?: Product;
  products?: Product[];
  order?: {
    orderNumber: string;
    customerName: string;
    address: string;
    city: string;
    district: string;
    phone: string;
  };
  mode: 'single' | 'bulk' | 'shipping';
  onClose?: () => void;
}

const templateIcons: Record<PrintTemplate, React.ReactNode> = {
  'product-label': <Tag className="w-5 h-5" />,
  'shelf-tag': <LayoutGrid className="w-5 h-5" />,
  'price-tag': <DollarSign className="w-5 h-5" />,
  'inventory-tag': <ClipboardList className="w-5 h-5" />,
  'shipping-label': <Package className="w-5 h-5" />,
  'bulk-product': <LayoutGrid className="w-5 h-5" />,
};

export default function BarcodePrintTemplates({ 
  product, 
  products, 
  order, 
  mode, 
  onClose 
}: BarcodePrintTemplatesProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<PrintTemplate | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printSuccess, setPrintSuccess] = useState(false);

  const availableTemplates: PrintTemplate[] = mode === 'bulk' 
    ? ['bulk-product']
    : mode === 'shipping'
    ? ['shipping-label']
    : ['product-label', 'shelf-tag', 'price-tag', 'inventory-tag'];

  const handlePrint = async (template: PrintTemplate) => {
    setIsPrinting(true);
    setPrintSuccess(false);

    try {
      const data: any = {};
      
      if (mode === 'bulk' && products) {
        data.products = products;
      } else if (mode === 'shipping' && order) {
        data.order = order;
      } else if (product) {
        data.product = product;
      }

      await downloadPrintTemplate(template, data);
      setPrintSuccess(true);
      setTimeout(() => setPrintSuccess(false), 2000);
    } catch (err) {
      console.error('Print error:', err);
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePreview = async (template: PrintTemplate) => {
    const data: any = {};
    
    if (mode === 'bulk' && products) {
      data.products = products;
    } else if (mode === 'shipping' && order) {
      data.order = order;
    } else if (product) {
      data.product = product;
    }

    const blob = await generatePrintTemplate(template, data);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Printer className="w-5 h-5 text-brand-red" />
          <h3 className="text-sm font-bold text-gray-900">Yazdırma Şablonları</h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Info */}
      {mode === 'bulk' && products && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
          <p className="text-xs text-blue-700">
            <strong>{products.length}</strong> ürün için etiket yazdırılacak
          </p>
        </div>
      )}

      {mode === 'shipping' && order && (
        <div className="px-4 py-2 bg-purple-50 border-b border-purple-100">
          <p className="text-xs text-purple-700">
            Sipariş <strong>{order.orderNumber}</strong> için kargo etiketi
          </p>
        </div>
      )}

      {mode === 'single' && product && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
          <p className="text-xs text-gray-600 truncate">{product.name}</p>
        </div>
      )}

      {/* Template Grid */}
      <div className="p-4 grid grid-cols-2 gap-3">
        {availableTemplates.map((templateId) => {
          const template = PRINT_TEMPLATES[templateId];
          const isSelected = selectedTemplate === templateId;

          return (
            <button
              key={templateId}
              onClick={() => setSelectedTemplate(isSelected ? null : templateId)}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                isSelected
                  ? 'border-brand-red bg-brand-red/5'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-lg ${
                isSelected ? 'bg-brand-red text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                {templateIcons[templateId]}
              </div>
              <div className="text-center">
                <p className={`text-xs font-semibold ${isSelected ? 'text-brand-red' : 'text-gray-900'}`}>
                  {template.name}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5">{template.description}</p>
              </div>
              <div className="text-[10px] text-gray-400">
                {template.width}×{template.height}mm
              </div>
            </button>
          );
        })}
      </div>

      {/* Actions */}
      {selectedTemplate && (
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <div className="flex gap-2">
            <button
              onClick={() => handlePreview(selectedTemplate)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-xl transition-colors"
            >
              <Printer className="w-4 h-4" />
              Önizleme
            </button>
            <button
              onClick={() => handlePrint(selectedTemplate)}
              disabled={isPrinting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-red hover:bg-red-700 disabled:bg-gray-300 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              {isPrinting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : printSuccess ? (
                <Check className="w-4 h-4" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {isPrinting ? 'İndiriliyor...' : printSuccess ? 'İndirildi!' : 'PDF İndir'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
